// src/services/queries/stage.ts
//
// React-query hooks para o domínio de Stages e StageTeams.

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryParams, Stage, StageTeam } from '@/model';
import { StageConverter } from '@/converters/stage';
import { StageService } from '../instances/stage';
import {
  PostStagesBatchRequest,
  PutStageRequest,
  PutStageCurrentRoundRequest,
  PostStageTeamRequest,
  PostStageBatchTeamsRequest,
} from '../request/stage';

/** GET /stages — lista paginada. */
export const useStages = (params?: QueryParams) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_STAGES', params], [params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StageService.getStages(params);
      if (err) throw err;
      return {
        data: StageConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      } as { data: Stage[]; meta: typeof data.meta };
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** GET /stages/:id — detalhe. */
export const useStageById = (id: string) => {
  const queryKey = useMemo(() => ['GET_STAGE_BY_ID', id], [id]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StageService.getStageById(id);
      if (err) throw err;
      return StageConverter.toDomainFromByIdResponse(data);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    query.refetch();
  }, [query]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** POST /stages/batch — cria N fases em uma transação. */
export const usePostStagesBatchMutation = () => {
  return useMutation({
    mutationKey: ['POST_STAGES_BATCH'],
    mutationFn: async (payload: PostStagesBatchRequest) => {
      const [err, data] = await StageService.postStagesBatch(payload);
      if (err) throw err;
      return StageConverter.toDomainFromPostBatchResponse(data);
    },
  });
};

/** PUT /stages/:id — atualiza todos os campos. */
export const usePutStageMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STAGE'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutStageRequest }) => {
      const [err, data] = await StageService.putStage(id, payload);
      if (err) throw err;
      return StageConverter.toDomainFromPutResponse(data);
    },
  });
};

/** PUT /stages/:id/current-round. */
export const usePutStageCurrentRoundMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STAGE_CURRENT_ROUND'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PutStageCurrentRoundRequest;
    }) => {
      const [err, data] = await StageService.putStageCurrentRound(id, payload);
      if (err) throw err;
      return StageConverter.toDomainFromPutCurrentRoundResponse(data);
    },
  });
};

/** PUT /stages/:id/main-widget — marca a fase como widget principal. */
export const usePutStageMainWidgetMutation = () => {
  return useMutation({
    mutationKey: ['PUT_STAGE_MAIN_WIDGET'],
    mutationFn: async (id: string) => {
      const [err, data] = await StageService.putStageMainWidget(id);
      if (err) throw err;
      return StageConverter.toDomainFromPutMainWidgetResponse(data);
    },
  });
};

/** DELETE /stages/:id. */
export const useDeleteStageMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_STAGE'],
    mutationFn: async (id: string) => {
      const [err] = await StageService.deleteStage(id);
      if (err) throw err;
      return;
    },
  });
};

/** GET /stages/:id/teams — paginado. */
export const useStageTeams = (id: string, params?: QueryParams) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_STAGE_TEAMS', id, params], [id, params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await StageService.getStageTeams(id, params);
      if (err) throw err;
      return {
        data: StageConverter.toDomainFromStageTeamsResponse(data),
        meta: data.meta,
      } as { data: StageTeam[]; meta: typeof data.meta };
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** POST /stages/:id/teams — associa um time. */
export const usePostStageTeamMutation = () => {
  return useMutation({
    mutationKey: ['POST_STAGE_TEAM'],
    mutationFn: async ({ id, payload }: { id: string; payload: PostStageTeamRequest }) => {
      const [err, data] = await StageService.postStageTeam(id, payload);
      if (err) throw err;
      return StageConverter.toDomainFromPostStageTeamResponse(data);
    },
  });
};

/** POST /stages/:id/teams/batch — associa vários times. */
export const usePostStageBatchTeamsMutation = () => {
  return useMutation({
    mutationKey: ['POST_STAGE_BATCH_TEAMS'],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: PostStageBatchTeamsRequest;
    }) => {
      const [err, data] = await StageService.postStageBatchTeams(id, payload);
      if (err) throw err;
      return StageConverter.toDomainFromPostStageBatchTeamsResponse(data);
    },
  });
};

/** DELETE /stage-teams/:uuid — remove a associação de um time com uma fase. */
export const useDeleteStageTeamMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_STAGE_TEAM'],
    mutationFn: async (uuid: string) => {
      const [err] = await StageService.deleteStageTeam(uuid);
      if (err) throw err;
      return;
    },
  });
};
